import { CurrencyAmount, State, Currency, USD_CURRENCY, FungibleTokenAmount, FungibleToken, Account, getInternalPlatformIdFromExternalPlatformId, notEmpty, InstrumentType, BalanceImpact, OrderSide, } from "@orb-labs/types";
import { CreateOperationsStatus } from "../enums.js";
import { extractPlatformInformation, hasError } from "../utils/utils.js";
export const extractSupportedPaymentMethods = (methods, paymentMethodsToExclude) => {
    if (!methods)
        return [];
    return methods
        .map((method) => ({
        paymentMethod: method.paymentMethod,
        title: method.title,
        subtitle: method.subtitle,
        icon: method.icon,
        imageUrl: method.imageUrl,
        platformIds: method.platformIds?.map((id) => getInternalPlatformIdFromExternalPlatformId(id)),
        orbyPlatformIds: method.platformIds ?? [],
        minimumAllowedAmount: CurrencyAmount.toCurrencyAmount(method.minimumAllowedAmount) ??
            CurrencyAmount.fromRawAmount(USD_CURRENCY, BigInt(2 * 10 ** 6)),
    }))
        .filter((method) => !paymentMethodsToExclude?.includes(method.paymentMethod));
};
export const extractTimeSeriesPoint = (point) => {
    if (!point || point.timestamp === undefined || point.value === undefined) {
        return undefined;
    }
    return {
        timestamp: new Date(point.timestamp),
        value: BigInt(point.value),
    };
};
export const extractTimeSeriesChange = (change) => {
    if (!change) {
        return undefined;
    }
    return {
        start: extractTimeSeriesPoint(change.start),
        end: extractTimeSeriesPoint(change.end),
        changeInPercentage: change.changeInPercentage,
    };
};
export const extractChangeByInterval = (changes) => {
    if (!changes) {
        return undefined;
    }
    return {
        fiveMinutes: extractTimeSeriesChange(changes.fiveMinutes),
        oneHour: extractTimeSeriesChange(changes.oneHour),
        fourHours: extractTimeSeriesChange(changes.fourHours),
        oneDay: extractTimeSeriesChange(changes.oneDay),
        sevenDays: extractTimeSeriesChange(changes.sevenDays),
        oneMonth: extractTimeSeriesChange(changes.oneMonth),
        sixMonths: extractTimeSeriesChange(changes.sixMonths),
        oneYear: extractTimeSeriesChange(changes.oneYear),
        all: extractTimeSeriesChange(changes.all),
    };
};
export const extractValueAndChanges = (balance) => {
    if (!balance) {
        return undefined;
    }
    return {
        fiatValue: CurrencyAmount.toCurrencyAmount(balance.fiatValue),
        fiatValueChanges: extractChangeByInterval(balance.fiatValueChanges),
    };
};
export const extractAuthenticationInformation = (authenticationInformation) => {
    if (!authenticationInformation) {
        return undefined;
    }
    return {
        method: authenticationInformation.method,
        value: authenticationInformation.value,
        externalUserId: authenticationInformation.externalUserId,
    };
};
export const extractUser = (user) => {
    if (!user) {
        return undefined;
    }
    return {
        id: user.id,
        authenticationInformation: extractAuthenticationInformation(user.authenticationInformation),
    };
};
export const extractAccountCluster = (accountCluster) => {
    if (!accountCluster) {
        return undefined;
    }
    else if (hasError(accountCluster)) {
        console.error("[extractAccountCluster]", accountCluster.code, accountCluster.message);
        return undefined;
    }
    return {
        accountClusterId: accountCluster?.accountClusterId,
        isPlatformAbstractionEnabled: accountCluster?.isPlatformAbstractionEnabled,
        idempotencyKey: accountCluster?.idempotencyKey,
        accounts: accountCluster?.accounts?.map((account) => Account.toAccount(account)),
        user: extractUser(accountCluster.user),
        // totalValueInFiat: extractValueAndChanges(accountCluster.totalValueInFiat),
        // balances: accountCluster?.balances?.map((balance: any) =>
        //   extractStandardizedBalance(balance)
        // ),
        // standardizedInstrumentPositions: extractStandardizedInstrumentPositions(
        //   accountCluster?.standardizedInstrumentPositions
        // ),
    };
};
export const extractOperationSet = (operationSet) => {
    if (!operationSet) {
        return undefined;
    }
    else if (hasError(operationSet)) {
        const err = hasError(operationSet);
        console.error("[extractOperationSet]", operationSet);
        return {
            status: CreateOperationsStatus.INTERNAL,
            errorMessage: err?.message,
            inputAmount: BigInt(0),
            outputAmount: BigInt(0),
        };
    }
    return {
        inputAmount: operationSet.inputAmount,
        outputAmount: operationSet.outputAmount,
        status: operationSet.status,
        primaryOperation: extractOnchainOperation(operationSet.primaryOperation),
        primaryOperationPreExistingState: State.toState(operationSet.primaryOperationPreExistingState),
        inputState: State.toState(operationSet.inputState),
        outputState: State.toState(operationSet.outputState),
        intents: operationSet.intents?.map((intent) => extractIntent(intent)),
        platformFee: CurrencyAmount.toCurrencyAmount(operationSet.platformFee),
        providerFees: CurrencyAmount.toCurrencyAmount(operationSet.providerFees),
        totalFee: CurrencyAmount.toCurrencyAmount(operationSet.totalFee),
        aggregateEstimatedTimeInMs: operationSet.aggregateEstimatedTimeInMs,
        accountAddress: operationSet.accountAddress,
        platformId: getInternalPlatformIdFromExternalPlatformId(operationSet.platformId),
    };
};
export const extractOnchainOperation = (onchainOperation) => {
    if (!onchainOperation) {
        return undefined;
    }
    return {
        platformId: getInternalPlatformIdFromExternalPlatformId(onchainOperation.platformId),
        data: onchainOperation.data,
        estimatedNetworkFees: CurrencyAmount.toCurrencyAmount(onchainOperation.estimatedNetworkFees),
        estimatedNetworkFeesInFiatCurrency: CurrencyAmount.toCurrencyAmount(onchainOperation.estimatedNetworkFeesInFiatCurrency),
        estimatedTimeInMs: onchainOperation.estimatedTimeInMs,
        format: onchainOperation.format,
        from: onchainOperation.from,
        gasLimit: onchainOperation.gasLimit ? BigInt(onchainOperation.gasLimit) : undefined,
        gasPrice: onchainOperation.gasPrice ? BigInt(onchainOperation.gasPrice) : undefined,
        inputState: State.toState(onchainOperation.inputState),
        inputAccountStates: State.toState(onchainOperation.inputAccountStates),
        maxFeePerGas: onchainOperation.maxFeePerGas ? BigInt(onchainOperation.maxFeePerGas) : undefined,
        maxPriorityFeePerGas: onchainOperation.maxPriorityFeePerGas
            ? BigInt(onchainOperation.maxPriorityFeePerGas)
            : undefined,
        nativeCurrency: Currency.toCurrency(onchainOperation.nativeCurrency),
        nonce: onchainOperation.nonce ? BigInt(onchainOperation.nonce) : undefined,
        outputState: State.toState(onchainOperation.outputState),
        outputAccountStates: State.toState(onchainOperation.outputAccountStates),
        to: onchainOperation.to,
        txRpcUrl: onchainOperation.txRpcUrl,
        type: onchainOperation.type,
        value: onchainOperation.value ? BigInt(onchainOperation.value) : undefined,
        origin: onchainOperation.origin,
        executionIndex: onchainOperation.executionIndex,
    };
};
export const extractIntent = (intent) => {
    if (!intent) {
        return undefined;
    }
    return {
        estimatedProtocolFees: FungibleTokenAmount.toFungibleTokenAmount(intent.estimatedProtocolFees),
        estimatedProtocolFeesInFiatCurrency: CurrencyAmount.toCurrencyAmount(intent.estimatedProtocolFeesInFiatCurrency),
        inputState: State.toState(intent.inputState),
        intentOperations: intent.intentOperations.map((operation) => extractOnchainOperation(operation)),
        outputState: State.toState(intent.outputState),
    };
};
export const extractOperationStatuses = (operationStatus) => {
    if (hasError(operationStatus)) {
        console.error("Error ", operationStatus);
        return undefined;
    }
    return operationStatus?.map((status) => extractOperationStatus(status));
};
export const extractOperationStatus = (status) => {
    if (!status) {
        return undefined;
    }
    return {
        blockHash: status.blockHash,
        blockNumber: status.blockNumber ? BigInt(status.blockNumber) : undefined,
        platformId: status.platformId ? getInternalPlatformIdFromExternalPlatformId(status.platformId) : undefined,
        errorReason: status.errorReason,
        hash: status.hash,
        id: status.id,
        status: status.status,
        type: status.type,
    };
};
export const extractActivities = (activities) => {
    if (!activities) {
        return undefined;
    }
    return activities.map((activity) => extractActivity(activity)).filter(notEmpty);
};
export const extractActivity = (activity) => {
    if (!activity) {
        return undefined;
    }
    return {
        aggregateFiatValueOfInputState: CurrencyAmount.toCurrencyAmount(activity.aggregateFiatValueOfInputState),
        aggregateFiatValueOfOutputState: CurrencyAmount.toCurrencyAmount(activity.aggregateFiatValueOfOutputState),
        platformFee: CurrencyAmount.toCurrencyAmount(activity.platformFee),
        providerFees: CurrencyAmount.toCurrencyAmount(activity.providerFees),
        totalFee: CurrencyAmount.toCurrencyAmount(activity.totalFee),
        gasTokenAmount: FungibleTokenAmount.toFungibleTokenAmount(activity.gasTokenAmount),
        gasTokenAmountInFiatCurrency: CurrencyAmount.toCurrencyAmount(activity.gasTokenAmountInFiatCurrency),
        category: activity.category,
        initiateAt: activity.initiateAt,
        inputState: State.toState(activity.inputState),
        operationStatuses: extractOperationStatuses(activity.operationStatuses),
        outputState: State.toState(activity.outputState),
        overallStatus: activity.overallStatus,
    };
};
// export const extractStandardizedBalances = (
//   balances?: any
// ): StandardizedBalance[] => {
//   return balances?.map((balance: any) => extractStandardizedBalance(balance));
// };
// export const extractStandardizedBalance = (
//   balance?: any
// ): StandardizedBalance => {
//   if (!balance) {
//     return undefined;
//   }
//   return {
//     standardizedInstrument: extractStandardizedInstrument(
//       balance.standardizedInstrument
//     ),
//     fungibleTokenBalancesOnPlatforms:
//       balance.fungibleTokenBalancesOnPlatforms?.map((ft: any) =>
//         FungibleTokenAmount.toFungibleTokenAmount(ft)
//       ) ?? [],
//     total: CurrencyAmount.toCurrencyAmount(balance.total),
//   };
// };
export const extractStandardizedInstrumentPositions = (positions) => positions?.map((position) => extractStandardizedInstrumentPosition(position)) ?? [];
export const extractStandardizedInstrumentPosition = (position) => {
    if (!position) {
        return undefined;
    }
    return {
        standardizedInstrument: extractStandardizedInstrument(position.standardizedInstrument),
        positionQuantitiesOnPlatforms: position.positionQuantitiesOnPlatforms?.map((ft) => FungibleTokenAmount.toFungibleTokenAmount(ft)) ?? [],
        total: CurrencyAmount.toCurrencyAmount(position.total),
    };
};
export const extractStandardizedFungibleTokens = (fungibleTokens) => fungibleTokens?.map((fungibleToken) => extractStandardizedFungibleToken(fungibleToken));
export const extractStandardizedPerpetuals = (perpetuals) => perpetuals?.map((perpetual) => extractStandardizedPerpetual(perpetual));
export const extractStandardizedPredictionEvents = (predictionEvents) => predictionEvents?.map((predictionEvent) => extractStandardizedPredictionEvent(predictionEvent));
export const extractStandardizedInstrument = (data) => {
    if (!data) {
        return undefined;
    }
    // Two raw shapes flow through here:
    // 1. The legacy "flat" shape — the wrapper carries top-level display fields
    //    (`displaySymbol`, `logoUrl`, etc.) plus an inner `instrument` that is
    //    always a `FungibleToken` (positions, supported-payment stablecoin).
    // 2. The Signal-feed / generic union shape — the wrapper carries `id` +
    //    `type` + an inner `instrument` that's a `StandardizedFungibleToken |
    //    StandardizedPerpetual | StandardizedPredictionEvent` discriminated by
    //    `__typename`.
    // For (2) we hydrate via the per-type extractor and the matching adapter
    // so the returned `StandardizedInstrument` carries `displaySymbol`,
    // `logoUrl`, `currentMarketCap`, `metric`, etc. populated identically to
    // every other instrument surface in the app.
    const inner = data.instrument;
    const innerTypename = inner?.__typename;
    if (innerTypename === "StandardizedFungibleToken") {
        return standardizedFungibleTokenToInstrument(extractStandardizedFungibleToken(inner));
    }
    else if (innerTypename === "StandardizedPerpetual") {
        return standardizedPerpetualToInstrument(extractStandardizedPerpetual(inner));
    }
    else if (innerTypename === "StandardizedPredictionEvent") {
        return standardizedPredictionEventToInstrument(extractStandardizedPredictionEvent(inner));
    }
    return {
        id: data.id,
        type: data.type,
        metric: extractTimeSeriesChange(data.metric),
        verified: data.verified,
        currentMarketCap: extractTimeSeriesChange(data.currentMarketCap),
        displaySymbol: data.displaySymbol,
        logoUrl: data.logoUrl,
        title: data.title,
        leverage: data.leverage,
        instrument: extractStandardizedFungibleToken(data.instrument),
    };
};
export const standardizedFungibleTokenToInstrument = (token) => {
    if (!token) {
        return undefined;
    }
    return {
        id: token.standardizedInstrumentId,
        type: InstrumentType.FUNGIBLE_TOKEN,
        metric: token.currentPrice,
        displaySymbol: token.currency.symbol,
        displayName: token.currency.name,
        instrument: token,
        verified: token.verified,
        currentMarketCap: token.currentMarketCap,
        logoUrl: token.currency.logoUrl,
    };
};
export const fungibleTokenPositionToInstrument = (position) => {
    if (!position) {
        return undefined;
    }
    const fungibleTokens = position.tokenBalancesOnPlatforms?.map((b) => b.token) ?? [];
    // Create a partial StandardizedFungibleToken with available data
    const partialFungibleToken = {
        standardizedInstrumentId: position.standardizedInstrumentId,
        currency: position.total.currency,
        verified: false,
        marketData: undefined,
        currentMarketCap: undefined,
        currentPrice: position.totalValueInFiat,
        currentTotalVolume: undefined,
        fungibleTokens,
        anchorToken: fungibleTokens[0],
        metadata: undefined,
    };
    return {
        id: position.standardizedInstrumentId,
        type: InstrumentType.FUNGIBLE_TOKEN,
        metric: position.totalValueInFiat,
        displaySymbol: position.total.currency.symbol,
        logoUrl: position.total.currency.logoUrl,
        instrument: partialFungibleToken,
        verified: false,
        currentMarketCap: undefined,
    };
};
export const standardizedPerpetualToInstrument = (perp) => {
    if (!perp) {
        return undefined;
    }
    return {
        id: perp.standardizedInstrumentId,
        type: InstrumentType.PERPETUAL_CONTRACT,
        logoUrl: perp.currency.logoUrl,
        metric: perp.currentPrice,
        displaySymbol: perp.currency.symbol,
        displayName: perp.currency.name,
        instrument: perp,
        verified: perp.verified,
        currentMarketCap: perp.currentTotalVolume,
        leverage: perp.leverage,
    };
};
export const standardizedPredictionEventToInstrument = (event) => {
    if (!event) {
        return undefined;
    }
    return {
        id: event.standardizedInstrumentId,
        type: InstrumentType.PREDICTION_MARKET,
        metric: event.volume,
        displaySymbol: event.title || "",
        displayName: event.title,
        title: event.title,
        logoUrl: event.imageUrl,
        instrument: event,
        verified: false,
        currentMarketCap: event.volume,
    };
};
export const perpPositionToInstrument = (position) => {
    if (!position) {
        return undefined;
    }
    const currency = position.size?.currency ?? Currency.toCurrency(undefined);
    const partialPerpetual = {
        standardizedInstrumentId: position.standardizedInstrumentId ?? "",
        currency,
        verified: false,
        currentPrice: undefined,
        currentTotalVolume: undefined,
        leverage: position.leverage ?? 1,
        markets: undefined,
        metadata: undefined,
    };
    return {
        id: position.standardizedInstrumentId ?? "",
        type: InstrumentType.PERPETUAL_CONTRACT,
        metric: undefined,
        displaySymbol: currency.symbol,
        displayName: currency.name,
        logoUrl: currency.logoUrl,
        instrument: partialPerpetual,
        verified: false,
        currentMarketCap: undefined,
        leverage: position.leverage,
    };
};
export const predictionOutcomePositionToInstrument = (position) => {
    if (!position) {
        return undefined;
    }
    const market = position.market;
    const imageUrl = market?.imageUrl ?? market?.image;
    const title = market?.question ?? position.eventTitle;
    const partialEvent = {
        id: position.standardizedInstrumentId ?? "",
        standardizedInstrumentId: position.standardizedInstrumentId ?? "",
        slug: "",
        title: title ?? "",
        volume: undefined,
        closesAt: market?.endDate,
        markets: notEmpty(market) ? [market] : [],
        imageUrl: imageUrl ?? "",
        eventType: undefined,
    };
    return {
        id: position.standardizedInstrumentId ?? "",
        type: InstrumentType.PREDICTION_MARKET,
        metric: undefined,
        displaySymbol: title ?? "",
        displayName: title,
        title,
        logoUrl: imageUrl,
        instrument: partialEvent,
        verified: false,
        currentMarketCap: undefined,
    };
};
export const extractStandardizedFungibleToken = (token) => {
    if (!token) {
        return undefined;
    }
    return {
        standardizedInstrumentId: token.standardizedInstrumentId,
        currency: Currency.toCurrency(token.currency),
        verified: token.verified,
        marketData: extractMarketData(token.marketData),
        currentMarketCap: extractTimeSeriesChange(token.currentMarketCap),
        currentPrice: extractTimeSeriesChange(token.currentPrice),
        priceChange1h: extractTimeSeriesChange(token.priceChange1h),
        currentTotalVolume: extractTimeSeriesChange(token.currentTotalVolume),
        fungibleTokens: token?.fungibleTokens?.map((ft) => FungibleToken.toFungibleToken(ft)) ?? [],
        anchorToken: FungibleToken.toFungibleToken(token.anchorToken),
        metadata: extractTokenMetadata(token.metadata),
        recentTrades: extractRecentTrades(token.recentTrades ?? token.marketData?.recentTrades),
        userMetadata: extractUserTokenMetadata(token.userMetadata),
        associatedPerpStandardizedInstrumentId: token.associatedPerpStandardizedInstrumentId,
    };
};
export const extractStandardizedPerpetual = (perp) => {
    if (!perp) {
        return undefined;
    }
    return {
        standardizedInstrumentId: perp.standardizedInstrumentId,
        currency: Currency.toCurrency(perp.currency),
        verified: perp.verified,
        currentPrice: extractTimeSeriesChange(perp.currentPrice),
        priceChange1h: extractTimeSeriesChange(perp.priceChange1h),
        oraclePrice: extractTimeSeriesChange(perp.oraclePrice),
        currentTotalVolume: extractTimeSeriesChange(perp.currentTotalVolume),
        leverage: perp.leverage,
        markets: extractPerpMarketDataArray(perp.markets),
        userMetadata: extractUserPerpMetadata(perp.userMetadata),
        metadata: extractPerpetualMetadata(perp.metadata),
    };
};
export const extractUserPredictionEventMetadata = (metadata) => {
    if (!metadata) {
        return undefined;
    }
    return {
        isWatching: metadata.isWatching || false,
    };
};
function extractSportDisplayLineValueOption(opt) {
    if (!notEmpty(opt?.market))
        return undefined;
    const value = opt.value != null ? Number(opt.value) : undefined;
    return { value, market: extractPredictionMarket(opt.market) };
}
function extractSportDisplaySectionContent(content) {
    if (!notEmpty(content?.baseTitle) || !Array.isArray(content.valueOptions))
        return undefined;
    const valueOptions = content.valueOptions.map(extractSportDisplayLineValueOption).filter(notEmpty);
    return {
        baseTitle: content.baseTitle,
        layoutType: content.layoutType,
        appendValueToTitle: Boolean(content.appendValueToTitle),
        valueOptions,
    };
}
function extractSportDisplaySection(section) {
    if (!notEmpty(section?.title))
        return undefined;
    const content = section.content.map(extractSportDisplaySectionContent).filter(notEmpty);
    return { title: section.title, defaultExpanded: Boolean(section.defaultExpanded), content };
}
export function extractSportDisplaySections(sections) {
    if (!sections)
        return [];
    return sections.map(extractSportDisplaySection).filter(notEmpty);
}
export const extractStandardizedPredictionEvent = (event) => {
    if (!event) {
        return undefined;
    }
    return {
        id: event.id,
        standardizedInstrumentId: event.standardizedInstrumentId,
        slug: event.slug,
        title: event.title,
        description: notEmpty(event.description) ? event.description : undefined,
        volume: extractTimeSeriesChange(event.volume),
        closesAt: event.closesAt ? new Date(event.closesAt) : undefined,
        markets: extractPredictionMarkets(event.markets),
        imageUrl: event.imageUrl,
        userMetadata: extractUserPredictionEventMetadata(event.userMetadata),
        eventType: event.eventType,
        sportMetadata: extractSportMetadata(event.sportMetadata),
        isSeries: event.isSeries,
        series: extractStandardizedPredictionEvents(event.series),
        sportDisplaySections: extractSportDisplaySections(event.sportDisplaySections),
    };
};
// ============ Perpetual Helper Functions ============
const extractPositionBucket = (bucket) => ({
    count: bucket?.count ?? 0,
    longsNotionalUsd: bucket?.longsNotionalUsd ?? 0,
    shortsNotionalUsd: bucket?.shortsNotionalUsd ?? 0,
    longsCount: bucket?.longsCount ?? 0,
    shortsCount: bucket?.shortsCount ?? 0,
});
export const extractPositionDistribution = (distribution) => {
    if (!distribution) {
        return undefined;
    }
    return {
        over5M: extractPositionBucket(distribution.over5M),
        between2_5MAnd5M: extractPositionBucket(distribution.between2_5MAnd5M),
        between1MAnd2_5M: extractPositionBucket(distribution.between1MAnd2_5M),
        between500KAnd1M: extractPositionBucket(distribution.between500KAnd1M),
        between250KAnd500K: extractPositionBucket(distribution.between250KAnd500K),
        between100KAnd250K: extractPositionBucket(distribution.between100KAnd250K),
        between50KAnd100K: extractPositionBucket(distribution.between50KAnd100K),
        between25KAnd50K: extractPositionBucket(distribution.between25KAnd50K),
        between10KAnd25K: extractPositionBucket(distribution.between10KAnd25K),
        between1KAnd10K: extractPositionBucket(distribution.between1KAnd10K),
        between250And1K: extractPositionBucket(distribution.between250And1K),
        under250: extractPositionBucket(distribution.under250),
        total: extractPositionBucket(distribution.total),
    };
};
export const extractPnlDistribution = (distribution) => {
    if (!distribution) {
        return undefined;
    }
    return {
        over1M: extractPositionBucket(distribution.over1M),
        between100KAnd1M: extractPositionBucket(distribution.between100KAnd1M),
        between10KAnd100K: extractPositionBucket(distribution.between10KAnd100K),
        between0And10K: extractPositionBucket(distribution.between0And10K),
        betweenNeg10KAnd0: extractPositionBucket(distribution.betweenNeg10KAnd0),
        betweenNeg100KAndNeg10K: extractPositionBucket(distribution.betweenNeg100KAndNeg10K),
        underNeg100K: extractPositionBucket(distribution.underNeg100K),
        total: extractPositionBucket(distribution.total),
    };
};
export const extractAccountSizeDistribution = (distribution) => {
    if (!distribution) {
        return undefined;
    }
    return {
        over5M: extractPositionBucket(distribution.over5M),
        between1MAnd5M: extractPositionBucket(distribution.between1MAnd5M),
        between500KAnd1M: extractPositionBucket(distribution.between500KAnd1M),
        between100KAnd500K: extractPositionBucket(distribution.between100KAnd500K),
        between50KAnd100K: extractPositionBucket(distribution.between50KAnd100K),
        between10KAnd50K: extractPositionBucket(distribution.between10KAnd50K),
        between250And10K: extractPositionBucket(distribution.between250And10K),
        under250: extractPositionBucket(distribution.under250),
        total: extractPositionBucket(distribution.total),
    };
};
export const extractPerpTradingData = (data) => {
    if (!data) {
        return undefined;
    }
    return {
        positionSize: extractPositionDistribution(data.positionSize),
        totalPnl: extractPnlDistribution(data.totalPnl),
        accountSize: extractAccountSizeDistribution(data.accountSize),
    };
};
export const extractPerpMarketDataArray = (markets) => {
    if (!markets || !Array.isArray(markets)) {
        return [];
    }
    return markets.map(extractPerpMarketData).filter(notEmpty);
};
export const extractPerpMarketData = (market) => {
    if (!market) {
        return undefined;
    }
    return {
        venue: market.venue,
        currentPrice: extractTimeSeriesChange(market.currentPrice),
        currentTotalVolume: extractTimeSeriesChange(market.currentTotalVolume),
        leverage: market.leverage,
        chartData: extractChartData(market.chartData),
        openInterest: extractTimeSeriesChange(market.openInterest),
        fundingRate: extractFundingRateInformation(market.fundingRate),
        recentTrades: extractPerpTrades(market.recentTrades),
        perpTradingData: extractPerpTradingData(market.tradingData),
    };
};
export const extractChartData = (chartData) => {
    if (!chartData) {
        return undefined;
    }
    const transformChartData = (data) => {
        if (!data || typeof data !== "object") {
            const defaultSummary = {
                buys: {
                    numberOfTransactions: BigInt(0),
                    totalVolume: BigInt(0),
                    numberOfUniqueAccounts: BigInt(0),
                },
                sells: {
                    numberOfTransactions: BigInt(0),
                    totalVolume: BigInt(0),
                    numberOfUniqueAccounts: BigInt(0),
                },
            };
            return {
                price: [],
                priceOHLC: [],
                volume: [],
                priceChangeInPercentage: 0,
                summary: defaultSummary,
            };
        }
        return {
            price: (data.price || []).map(extractTimeSeriesPoint),
            priceOHLC: (data.priceOHLC || []).map((ohlc) => ({
                timestamp: new Date(ohlc.timestamp),
                open: BigInt(ohlc.open?.toString() || "0"),
                high: BigInt(ohlc.high?.toString() || "0"),
                low: BigInt(ohlc.low?.toString() || "0"),
                close: BigInt(ohlc.close?.toString() || "0"),
            })),
            volume: (data.volume || []).map(extractTimeSeriesPoint),
            priceChangeInPercentage: data.priceChangeInPercentage || 0,
            summary: data.summary
                ? {
                    buys: {
                        numberOfTransactions: BigInt(data.summary.buys?.numberOfTransactions || 0),
                        totalVolume: BigInt(data.summary.buys?.totalVolume || 0),
                        numberOfUniqueAccounts: BigInt(data.summary.buys?.numberOfUniqueAccounts || 0),
                    },
                    sells: {
                        numberOfTransactions: BigInt(data.summary.sells?.numberOfTransactions || 0),
                        totalVolume: BigInt(data.summary.sells?.totalVolume || 0),
                        numberOfUniqueAccounts: BigInt(data.summary.sells?.numberOfUniqueAccounts || 0),
                    },
                }
                : {
                    buys: {
                        numberOfTransactions: BigInt(0),
                        totalVolume: BigInt(0),
                        numberOfUniqueAccounts: BigInt(0),
                    },
                    sells: {
                        numberOfTransactions: BigInt(0),
                        totalVolume: BigInt(0),
                        numberOfUniqueAccounts: BigInt(0),
                    },
                },
        };
    };
    return {
        fiveMinutes: transformChartData(chartData.fiveMinutes),
        oneHour: transformChartData(chartData.oneHour),
        fourHours: transformChartData(chartData.fourHours),
        oneDay: transformChartData(chartData.oneDay),
        sevenDays: transformChartData(chartData.sevenDays),
        oneMonth: transformChartData(chartData.oneMonth),
        sixMonths: transformChartData(chartData.sixMonths),
        oneYear: transformChartData(chartData.oneYear),
        all: transformChartData(chartData.all),
    };
};
export const extractFundingRateInformation = (fundingRate) => {
    if (!fundingRate) {
        return undefined;
    }
    return {
        rate: extractTimeSeriesChange(fundingRate.rate),
        timestamp: fundingRate.timestamp ? new Date(fundingRate.timestamp) : undefined,
    };
};
export const extractPerpTrades = (trades) => {
    if (!trades || !Array.isArray(trades)) {
        return [];
    }
    return trades.map(extractPerpTrade).filter(notEmpty);
};
export const extractPerpTrade = (trade) => {
    if (!trade) {
        return undefined;
    }
    // Accept the `perp`-prefixed aliases used by the Signal feed query
    // (where the activity-item union forces per-variant field aliasing to
    // satisfy codegen) as well as the canonical base names used by every
    // other GetActivityHistory / perps surface. Mirrors the alias-aware
    // pattern in `extractPredictionTradeItem`.
    return {
        venue: trade.perpVenue ?? trade.venue,
        symbol: trade.perpSymbol ?? trade.symbol,
        tradeId: trade.perpTradeId ?? trade.tradeId,
        orderId: trade.orderId,
        side: trade.perpSide ?? trade.side,
        price: CurrencyAmount.toCurrencyAmount(trade.perpPrice ?? trade.price),
        size: CurrencyAmount.toCurrencyAmount(trade.perpSize ?? trade.size),
        value: CurrencyAmount.toCurrencyAmount(trade.perpValue ?? trade.value),
        fee: CurrencyAmount.toCurrencyAmount(trade.fee),
        closedPnl: CurrencyAmount.toCurrencyAmount(trade.closedPnl),
        positionBefore: CurrencyAmount.toCurrencyAmount(trade.positionBefore),
        isMaker: trade.isMaker,
        timestamp: trade.perpTimestamp ?? trade.timestamp ? new Date(trade.perpTimestamp ?? trade.timestamp) : undefined,
        leverage: trade.leverage,
        positionDirection: trade.positionDirection,
        positionAction: trade.positionAction,
    };
};
export const extractUserPerpMetadata = (metadata) => {
    if (!metadata) {
        return undefined;
    }
    return {
        collaterals: extractCollateralBalances(metadata.collaterals),
        margin: extractMarginSummary(metadata.margin),
        openOrders: extractPerpOrders(metadata.openOrders),
        positions: extractPerpPositions(metadata.positions),
        isWatching: metadata.isWatching || false,
    };
};
export const extractCollateralBalances = (collaterals) => {
    if (!collaterals || !Array.isArray(collaterals)) {
        return [];
    }
    return collaterals.map(extractCollateralBalance).filter(notEmpty);
};
export const extractCollateralBalance = (collateral) => {
    if (!collateral) {
        return undefined;
    }
    return {
        currency: Currency.toCurrency(collateral.currency),
        total: collateral.total ? BigInt(collateral.total) : BigInt(0),
        available: collateral.available ? BigInt(collateral.available) : BigInt(0),
        held: collateral.held ? BigInt(collateral.held) : BigInt(0),
        usdValue: CurrencyAmount.toCurrencyAmount(collateral.usdValue),
    };
};
export const extractMarginSummary = (margin) => {
    if (!margin) {
        return undefined;
    }
    return {
        timestamp: margin.timestamp ? new Date(margin.timestamp) : undefined,
        equityUsd: CurrencyAmount.toCurrencyAmount(margin.equityUsd),
        freeMarginUsd: CurrencyAmount.toCurrencyAmount(margin.freeMarginUsd),
        marginUsedUsd: CurrencyAmount.toCurrencyAmount(margin.marginUsedUsd),
        maintenanceMarginUsd: CurrencyAmount.toCurrencyAmount(margin.maintenanceMarginUsd),
        initialMarginUsd: CurrencyAmount.toCurrencyAmount(margin.initialMarginUsd),
        leverage: margin.leverage,
        withdrawableUsd: CurrencyAmount.toCurrencyAmount(margin.withdrawableUsd),
        totalNotionalUsd: CurrencyAmount.toCurrencyAmount(margin.totalNotionalUsd),
    };
};
export const extractPerpOrders = (orders) => {
    if (!orders || !Array.isArray(orders)) {
        return [];
    }
    return orders.map(extractPerpOrder).filter(notEmpty);
};
export const extractPerpOrder = (order) => {
    if (!order) {
        return undefined;
    }
    return {
        venue: order.venue,
        symbol: order.symbol,
        orderId: order.orderId,
        clientOrderId: order.clientOrderId,
        side: order.side,
        type: order.type,
        price: CurrencyAmount.toCurrencyAmount(order.price),
        size: CurrencyAmount.toCurrencyAmount(order.size),
        filledSize: CurrencyAmount.toCurrencyAmount(order.filledSize),
        status: order.status,
        createdAt: order.createdAt ? new Date(order.createdAt) : undefined,
        updatedAt: order.updatedAt ? new Date(order.updatedAt) : undefined,
        reduceOnly: order.reduceOnly,
    };
};
export const extractPerpPositions = (positions) => {
    if (!positions || !Array.isArray(positions)) {
        return [];
    }
    return positions.map(extractPerpPosition).filter(notEmpty);
};
export const extractPerpPosition = (position) => {
    if (!position) {
        return undefined;
    }
    return {
        standardizedInstrumentId: position.standardizedInstrumentId,
        side: position.side,
        size: CurrencyAmount.toCurrencyAmount(position.size),
        entryPrice: CurrencyAmount.toCurrencyAmount(position.entryPrice),
        markPrice: CurrencyAmount.toCurrencyAmount(position.markPrice),
        indexPrice: CurrencyAmount.toCurrencyAmount(position.indexPrice),
        notionalUsd: CurrencyAmount.toCurrencyAmount(position.notionalUsd),
        unrealizedPnlUsd: CurrencyAmount.toCurrencyAmount(position.unrealizedPnlUsd),
        realizedPnlUsd: CurrencyAmount.toCurrencyAmount(position.realizedPnlUsd),
        cumulativeFundingUsd: CurrencyAmount.toCurrencyAmount(position.cumulativeFundingUsd),
        leverage: position.leverage,
        leverageType: position.leverageType,
        liquidationPrice: CurrencyAmount.toCurrencyAmount(position.liquidationPrice),
        marginUsed: CurrencyAmount.toCurrencyAmount(position.marginUsed),
        returnOnEquity: position.returnOnEquity,
    };
};
// ============ Prediction Market Helper Functions ============
export const extractPredictionMarkets = (markets) => {
    if (!markets || !Array.isArray(markets)) {
        return [];
    }
    return markets.map(extractPredictionMarket).filter(notEmpty);
};
export const extractPredictionMarket = (market) => {
    if (!market) {
        return undefined;
    }
    return {
        metadata: {
            externalId: market.metadata?.externalId ?? market.externalId,
            conditionId: market.metadata?.conditionId ?? market.conditionId,
            venue: market.metadata?.venue ?? market.venue,
            tokenIds: market.metadata?.tokenIds ?? market.tokenIds ?? [],
        },
        venueMarketRef: market.venueMarketRef,
        question: market.question,
        image: market.image,
        startDate: market.startDate ? new Date(market.startDate) : undefined,
        endDate: market.endDate ? new Date(market.endDate) : undefined,
        status: market.status,
        volume: extractTimeSeriesChange(market.volume),
        outcomes: extractPredictionMarketOutcomes(market.outcomes),
        description: market.description,
        resolutionSource: market.resolutionSource,
        imageUrl: market.imageUrl,
        groupItemTitle: market.groupItemTitle,
        sportsMarketType: market.sportsMarketType,
        settlementToken: market.settlementToken ? FungibleToken.toFungibleToken(market.settlementToken) : undefined,
    };
};
export const extractPredictionMarketOutcomes = (outcomes) => {
    if (!outcomes || !Array.isArray(outcomes)) {
        return [];
    }
    return outcomes.map(extractPredictionMarketOutcome).filter(notEmpty);
};
export const extractTeamMetadata = (team) => {
    if (!team) {
        return undefined;
    }
    return {
        name: team.name,
        abbreviation: team.abbreviation,
        league: team.league,
        logo: team.logo,
        color: team.color,
        record: team.record,
    };
};
export const extractSportMetadata = (sport) => {
    if (!sport) {
        return undefined;
    }
    return {
        isLive: sport.isLive ?? false,
        gameStatus: sport.gameStatus,
        sportType: sport.sportType,
        league: sport.league,
        scheduledStartTime: sport.scheduledStartTime ? new Date(sport.scheduledStartTime) : undefined,
        homeTeam: extractTeamMetadata(sport.homeTeam),
        awayTeam: extractTeamMetadata(sport.awayTeam),
        homeTeamScore: sport.homeTeamScore,
        awayTeamScore: sport.awayTeamScore,
        period: sport.period,
        elapsed: sport.elapsed,
        homeTeamWin: extractPredictionMarketOutcome(sport.homeTeamWin),
        awayTeamWin: extractPredictionMarketOutcome(sport.awayTeamWin),
        draw: extractPredictionMarketOutcome(sport.draw),
    };
};
export const extractPredictionMarketOutcome = (outcome) => {
    if (!outcome) {
        return undefined;
    }
    return {
        externalTokenId: outcome.externalTokenId,
        label: outcome.label,
        price: extractTimeSeriesChange(outcome.price),
        color: outcome.color,
        chartData: extractChartData(outcome.chartData),
        outcomeShareDecimals: outcome.outcomeShareDecimals,
        // imageUrl: outcome.imageUrl,
        // abbreviation: outcome.abbreviation ?? "",
        // teamMetadata: extractTeamMetadata(outcome.teamMetadata),
    };
};
export const extractPredictionTradeItem = (raw) => {
    if (!raw) {
        return undefined;
    }
    const outcome = extractPredictionMarketOutcome(raw.predOutcome ?? raw.outcome);
    if (!outcome) {
        return undefined;
    }
    const sideStr = raw.predSide ?? raw.side;
    let side;
    if (sideStr === "BUY" || sideStr === OrderSide.BUY) {
        side = OrderSide.BUY;
    }
    else if (sideStr === "SELL" || sideStr === OrderSide.SELL) {
        side = OrderSide.SELL;
    }
    const balanceImpactStr = raw.predBalanceImpact ?? raw.balanceImpact;
    let balanceImpact;
    if (balanceImpactStr === "POSITIVE" || balanceImpactStr === BalanceImpact.POSITIVE) {
        balanceImpact = BalanceImpact.POSITIVE;
    }
    else if (balanceImpactStr === "NEGATIVE" || balanceImpactStr === BalanceImpact.NEGATIVE) {
        balanceImpact = BalanceImpact.NEGATIVE;
    }
    else if (notEmpty(balanceImpactStr)) {
        balanceImpact = BalanceImpact.NONE;
    }
    const tsRaw = raw.predTimestamp ?? raw.timestamp;
    return {
        timestamp: tsRaw instanceof Date ? tsRaw : new Date(tsRaw ?? Date.now()),
        outcome,
        side,
        quantity: raw.quantity,
        price: raw.predPrice ?? raw.price,
        fiatValue: raw.fiatValue,
        balanceImpact,
        eventTitle: raw.eventTitle,
        transactionHash: raw.transactionHash,
        pnl: raw.pnl,
        pnlPercent: raw.pnlPercent,
    };
};
export const extractPredictionOrder = (raw) => {
    if (!raw) {
        return undefined;
    }
    const outcome = extractPredictionMarketOutcome(raw.orderOutcome ?? raw.outcome);
    if (!outcome) {
        return undefined;
    }
    const sideStr = raw.orderSide ?? raw.side;
    let side;
    if (sideStr === "BUY" || sideStr === OrderSide.BUY) {
        side = OrderSide.BUY;
    }
    else if (sideStr === "SELL" || sideStr === OrderSide.SELL) {
        side = OrderSide.SELL;
    }
    else {
        return undefined;
    }
    return {
        venue: raw.orderVenue ?? raw.venue,
        orderId: raw.orderId,
        outcome,
        side,
        price: raw.orderPrice ?? raw.price,
        size: raw.orderSize ?? raw.size,
        eventTitle: raw.eventTitle,
    };
};
/**
 * Transforms recent trades from GraphQL response format to Trade[] type
 */
export const extractRecentTrades = (recentTrades) => {
    if (!recentTrades || !Array.isArray(recentTrades)) {
        return [];
    }
    return recentTrades.map((trade) => {
        // Use toFungibleTokenAmount to properly create the amount
        const amount = FungibleTokenAmount.toFungibleTokenAmount(trade.amount);
        // Convert balanceImpact string to enum
        let balanceImpact;
        const balanceImpactStr = trade.balanceImpact || "";
        if (balanceImpactStr === "positive") {
            balanceImpact = BalanceImpact.POSITIVE;
        }
        else if (balanceImpactStr === "negative") {
            balanceImpact = BalanceImpact.NEGATIVE;
        }
        else {
            balanceImpact = BalanceImpact.NONE;
        }
        return {
            accountAddress: trade.accountAddress,
            amount,
            balanceImpact,
            fiatPnl: trade.fiatPnl ? CurrencyAmount.toCurrencyAmount(trade.fiatPnl) : undefined,
            timestamp: new Date(trade.timestamp || Date.now()),
        };
    });
};
export const extractUserTokenMetadata = (userMetadata) => {
    if (!userMetadata) {
        return undefined;
    }
    return {
        currentBalance: CurrencyAmount.toCurrencyAmount(userMetadata.currentBalance),
        valueHistory: extractValueAndChanges(userMetadata.valueHistory),
        isWatching: userMetadata.isWatching || false,
    };
};
function optionalFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
/**
 * Extracts and transforms token metadata from GetStandardizedTokenQuery response
 */
export const extractTokenMetadata = (metadata) => {
    if (!metadata)
        return undefined;
    return {
        description: notEmpty(metadata.description) ? metadata.description : undefined,
        socials: metadata.socials?.map((social) => ({
            platform: social.platform,
            url: social.url,
        })) || [],
        verified: false,
        createdAt: metadata.createdAt ? new Date(metadata.createdAt) : undefined,
        launchpad: notEmpty(metadata.launchpad) ? metadata.launchpad : undefined,
        lifecycleStage: notEmpty(metadata.lifecycleStage) ? metadata.lifecycleStage : undefined,
        bondingCurveProgress: optionalFiniteNumber(metadata.bondingCurveProgress),
        holders: optionalFiniteNumber(metadata.holders),
        txCount: optionalFiniteNumber(metadata.txCount),
        uniqueTraders: optionalFiniteNumber(metadata.uniqueTraders),
        confidenceScore: optionalFiniteNumber(metadata.confidenceScore),
        devHoldingPercent: optionalFiniteNumber(metadata.devHoldingPercent),
        sniperHoldingPercent: optionalFiniteNumber(metadata.sniperHoldingPercent),
        top10HoldersPercent: optionalFiniteNumber(metadata.top10HoldersPercent),
        bundleHoldingPercent: optionalFiniteNumber(metadata.bundleHoldingPercent),
        insiderHoldingPercent: optionalFiniteNumber(metadata.insiderHoldingPercent),
        buyCount: optionalFiniteNumber(metadata.buyCount),
        sellCount: optionalFiniteNumber(metadata.sellCount),
    };
};
/**
 * Extracts and transforms perpetual metadata from GetStandardizedPerpetualQuery response
 */
export const extractPerpetualMetadata = (metadata) => {
    if (!metadata)
        return undefined;
    return {
        description: metadata.description || undefined,
        socials: metadata.socials?.map((social) => ({
            platform: social.platform,
            url: social.url,
        })) || [],
        verified: metadata.verified ?? false,
    };
};
/**
 * Extracts and transforms market data from GetStandardizedTokenQuery response
 * This has more complete chart data than the list query
 */
export const extractMarketData = (marketData) => {
    if (!marketData)
        return undefined;
    const defaultTimeSeriesChange = {
        start: { timestamp: new Date(), value: BigInt(0) },
        end: { timestamp: new Date(), value: BigInt(0) },
        changeInPercentage: 0,
    };
    return {
        chartData: extractChartData(marketData.chartData),
        circulatingSupply: extractTimeSeriesChange(marketData.circulatingSupply) || defaultTimeSeriesChange,
        recentTrades: extractRecentTrades(marketData.recentTrades),
        totalHolders: extractTimeSeriesChange(marketData.totalHolders) || defaultTimeSeriesChange,
        top10HoldersPercentage: marketData.top10HoldersPercentage || 0,
    };
};
export const extractStandardizedFungibleTokenCategories = (data) => {
    if (!data || !Array.isArray(data)) {
        return [];
    }
    return data.map((categoryData) => extractStandardizedFungibleTokenCategory(categoryData)).filter(notEmpty);
};
export const extractStandardizedFungibleTokenCategory = (categoryData) => {
    if (!categoryData) {
        return undefined;
    }
    return {
        category: categoryData.category,
        description: categoryData.description,
        similarFungibleTokens: (categoryData.similarFungibleTokens || [])
            .map((token) => extractStandardizedFungibleToken(token))
            .filter(notEmpty),
    };
};
export const extractStandardizedPerpetualCategories = (data) => {
    if (!data || !Array.isArray(data)) {
        return [];
    }
    return data.map((categoryData) => extractStandardizedPerpetualCategory(categoryData)).filter(notEmpty);
};
export const extractStandardizedPerpetualCategory = (categoryData) => {
    if (!categoryData) {
        return undefined;
    }
    return {
        category: categoryData.category,
        description: categoryData.description,
        similarPerps: (categoryData.similarPerps || [])
            .map((perp) => extractStandardizedPerpetual(perp))
            .filter(notEmpty),
    };
};
export const extractStandardizedPredictionEventCategories = (data) => {
    if (!data || !Array.isArray(data)) {
        return [];
    }
    return data.map((categoryData) => extractStandardizedPredictionEventCategory(categoryData)).filter(notEmpty);
};
export const extractStandardizedPredictionEventCategory = (categoryData) => {
    if (!categoryData) {
        return undefined;
    }
    return {
        category: categoryData.category,
        description: categoryData.description,
        similarPredictions: (categoryData.similarPredictions || [])
            .map((prediction) => extractStandardizedPredictionEvent(prediction))
            .filter(notEmpty),
    };
};
export const extractPlatformInformations = (platformInformation) => platformInformation?.map((info) => extractPlatformInformation(info));
export const extractMaxAmountPerInterval = (maxAmountPerInterval) => {
    if (!maxAmountPerInterval) {
        return undefined;
    }
    return {
        amount: maxAmountPerInterval.amount,
        interval: {
            amount: maxAmountPerInterval.interval.amount,
            unit: maxAmountPerInterval.interval.unit,
        },
    };
};
export const extractAllowlistWithOpenFallback = (allowlist) => {
    if (!allowlist) {
        return undefined;
    }
    return {
        tokenAllowlist: {
            tokenAllowlistType: allowlist.tokenAllowlist.tokenAllowlistType,
            sponsoredFunctions: allowlist.tokenAllowlist.sponsoredFunctions,
        },
        arbitraryContractAllowlist: allowlist.arbitraryContractAllowlist,
    };
};
export const extractGasSponsorshipPolicy = (gasSponsorshipPolicy) => {
    if (!gasSponsorshipPolicy) {
        return undefined;
    }
    return {
        maxTotalGasSpendForInstance: extractMaxAmountPerInterval(gasSponsorshipPolicy.maxTotalGasSpendForInstance),
        maxGasSpendPerTransaction: gasSponsorshipPolicy.maxGasSpendPerTransaction,
        allowlistWithOpenFallback: extractAllowlistWithOpenFallback(gasSponsorshipPolicy.allowlistWithOpenFallback),
        maxTotalTransactionCountForInstance: extractMaxAmountPerInterval(gasSponsorshipPolicy.maxTotalTransactionCountForInstance),
        maxTotalGasSpendPerUser: extractMaxAmountPerInterval(gasSponsorshipPolicy.maxTotalGasSpendPerUser),
        maxTotalTransactionCountPerUser: extractMaxAmountPerInterval(gasSponsorshipPolicy.maxTotalTransactionCountPerUser),
    };
};
export const extractGasSpendForInstances = (gasSpendForInstances) => gasSpendForInstances?.map((data) => extractGasSpendForInstance(data));
export const extractGasSpendForInstance = (gasSpendForInstance) => {
    if (!gasSpendForInstance) {
        return undefined;
    }
    return {
        instanceName: gasSpendForInstance.instanceName,
        gasSpentForInstance: extractGasSponsorshipData(gasSpendForInstance.gasSpentForInstance),
    };
};
export const extractGasSponsorshipInformation = (gasSponsorshipData) => gasSponsorshipData?.map((data) => extractGasSponsorshipData(data));
export const extractGasSponsorshipData = (gasSponsorshipData) => {
    if (!gasSponsorshipData) {
        return undefined;
    }
    return {
        totalGasSpent: CurrencyAmount.toCurrencyAmount(gasSponsorshipData.totalGasSpent),
        totalTransactionCount: gasSponsorshipData.totalTransactionCount,
    };
};
export const extractStablecoinAutoDepositSettingsForAccount = (settings) => {
    if (!settings) {
        return undefined;
    }
    return settings.map((setting) => extractStablecoinAutoDepositSettingsForAccountItem(setting)).filter(notEmpty);
};
export const extractStablecoinAutoDepositSettingsForAccountItem = (setting) => {
    if (!setting) {
        return undefined;
    }
    return {
        account: Account.toAccount(setting.account),
        settings: extractStablecoinAutoDepositPerPlatformUserStablecoinsSettings(setting.settings),
    };
};
export const extractStablecoinAutoDepositPerPlatformUserStablecoinsSettings = (settings) => {
    if (!settings) {
        return undefined;
    }
    return settings
        .map((setting) => extractStablecoinAutoDepositPerPlatformUserStablecoinsSettingsItem(setting))
        .filter(notEmpty);
};
export const extractStablecoinAutoDepositPerPlatformUserStablecoinsSettingsItem = (setting) => {
    if (!setting) {
        return undefined;
    }
    return {
        userStablecoins: extractStablecoinAutoDepositProportions(setting.userStablecoins),
        platformId: getInternalPlatformIdFromExternalPlatformId(setting.platformId),
        miniProgram: setting.miniProgram,
        miniProgramHash: setting.miniProgramHash,
    };
};
export const extractStablecoinAutoDepositProportions = (proportions) => {
    if (!proportions) {
        return undefined;
    }
    return proportions.map((proportion) => extractStablecoinAutoDepositProportion(proportion)).filter(notEmpty);
};
export const extractStablecoinAutoDepositProportion = (proportion) => {
    if (!proportion) {
        return undefined;
    }
    return {
        token: FungibleToken.toFungibleToken(proportion.token),
        targetPercentage: proportion.targetPercentage,
    };
};
// ============ Portfolio Position Helper Functions ============
/**
 * Extracts ValueAndChanges from GraphQL response that uses 'value'/'valueChanges' field names
 */
export const extractValueAndChangesFromResponse = (data) => {
    if (!data) {
        return undefined;
    }
    return {
        fiatValue: CurrencyAmount.toCurrencyAmount(data.value || data.fiatValue),
        fiatValueChanges: extractChangeByInterval(data.valueChanges || data.fiatValueChanges),
    };
};
export const extractFungibleTokenPosition = (position) => {
    if (!position) {
        return undefined;
    }
    return {
        standardizedInstrumentId: position.standardizedInstrumentId,
        tokenBalancesOnPlatforms: position.tokenBalancesOnPlatforms?.map((balance) => FungibleTokenAmount.toFungibleTokenAmount(balance)) ??
            [],
        totalValueInFiat: extractTimeSeriesChange(position.totalValueInFiat),
        total: CurrencyAmount.toCurrencyAmount(position.total),
        // Fall through `undefined` rather than defaulting to `false`: older
        // cached payloads predating the schema addition omit the field, and
        // misreporting them as "not verified" is worse than declining to claim.
        verified: typeof position.verified === "boolean" ? position.verified : undefined,
        // amountHistory: extractValueAndChangesFromResponse(position.amountHistory),
    };
};
export const extractFungibleTokenPositions = (positions) => {
    if (!positions || !Array.isArray(positions)) {
        return [];
    }
    return positions.map(extractFungibleTokenPosition).filter(notEmpty);
};
export const extractCrossMarginPosition = (position) => {
    if (!position) {
        return undefined;
    }
    return {
        positions: extractPerpPositions(position.positions),
        marginSummary: extractMarginSummary(position.marginSummary),
        venue: position.venue,
        totalValueInFiat: extractTimeSeriesChange(position.totalValueInFiat),
    };
};
export const extractCrossMarginPositions = (positions) => {
    if (!positions || !Array.isArray(positions)) {
        return [];
    }
    return positions.map(extractCrossMarginPosition).filter(notEmpty);
};
export const extractIsolatedMarginPosition = (position) => {
    if (!position) {
        return undefined;
    }
    return {
        position: extractPerpPosition(position.position),
        marginSummary: extractMarginSummary(position.marginSummary),
        venue: position.venue,
        totalValueInFiat: extractTimeSeriesChange(position.totalValueInFiat),
    };
};
export const extractIsolatedMarginPositions = (positions) => {
    if (!positions || !Array.isArray(positions)) {
        return [];
    }
    return positions.map(extractIsolatedMarginPosition).filter(notEmpty);
};
export const extractPerpetualPosition = (position) => {
    if (!position) {
        return undefined;
    }
    return {
        crossMarginPositions: extractCrossMarginPositions(position.crossMarginPositions),
        isolatedMarginPositions: extractIsolatedMarginPositions(position.isolatedMarginPositions),
        totalValueInFiat: extractTimeSeriesChange(position.totalValueInFiat),
    };
};
export const extractPerpetualPositions = (positions) => {
    if (!positions || !Array.isArray(positions)) {
        return [];
    }
    return positions.map(extractPerpetualPosition).filter(notEmpty);
};
export const extractPredictionOutcomePosition = (position) => {
    if (!position) {
        return undefined;
    }
    return {
        standardizedInstrumentId: position.standardizedInstrumentId,
        eventTitle: position.eventTitle,
        market: extractPredictionMarket(position.market),
        outcomeIndex: position.outcomeIndex,
        size: position.size,
        avgEntryPrice: position.avgEntryPrice,
        pnl: CurrencyAmount.toCurrencyAmount(position.pnl),
        pnlPercent: position.pnlPercent,
    };
};
export const extractPredictionOutcomePositions = (positions) => {
    if (!positions || !Array.isArray(positions)) {
        return [];
    }
    return positions.map(extractPredictionOutcomePosition).filter(notEmpty);
};
export const extractPortfolioFungibleTokenPositions = (data) => {
    if (!data) {
        return undefined;
    }
    return {
        positions: extractFungibleTokenPositions(data.positions),
        totalValueInFiat: extractTimeSeriesChange(data.totalValueInFiat),
    };
};
export const extractPortfolioPerpetualPositions = (data) => {
    if (!data) {
        return undefined;
    }
    return {
        crossMarginPositions: extractCrossMarginPositions(data.crossMarginPositions),
        isolatedMarginPositions: extractIsolatedMarginPositions(data.isolatedMarginPositions),
        totalValueInFiat: extractTimeSeriesChange(data.totalValueInFiat),
    };
};
export const extractPortfolioPredictionPositions = (data) => {
    if (!data) {
        return undefined;
    }
    return {
        positions: extractPredictionOutcomePositions(data.positions),
        totalValueInFiat: extractTimeSeriesChange(data.totalValueInFiat),
    };
};
export const extractPositions = (data) => {
    if (!data) {
        return undefined;
    }
    return {
        fungibleTokenPositions: extractPortfolioFungibleTokenPositions(data.fungibleTokenPositions),
        perpetualPositions: extractPortfolioPerpetualPositions(data.perpetualPositions),
        predictionPositions: extractPortfolioPredictionPositions(data.predictionPositions),
        stablecoinPositions: extractPortfolioFungibleTokenPositions(data.stablecoinPositions),
        totalValueInFiat: extractTimeSeriesChange(data.totalValueInFiat),
        valueHistory: extractChartData(data.valueHistory),
    };
};
export const extractFormFieldValidation = (validation) => {
    if (!validation)
        return undefined;
    return {
        type: validation.type,
        value: validation.value,
        message: validation.message,
    };
};
export const extractFormFieldOption = (option) => {
    if (!option)
        return undefined;
    return {
        value: option.value,
        label: option.label,
        disabled: option.disabled,
    };
};
export const extractFormFieldDependency = (dependency) => {
    if (!dependency)
        return undefined;
    return {
        fieldId: dependency.fieldId,
        values: dependency.values,
    };
};
export const extractFormFieldConfig = (field) => {
    if (!field)
        return undefined;
    return {
        id: field.id,
        type: field.type,
        label: field.label,
        placeholder: field.placeholder,
        helperText: field.helperText,
        required: field.required,
        validations: field.validations?.map(extractFormFieldValidation).filter(notEmpty) ?? [],
        options: field.options?.map(extractFormFieldOption).filter(notEmpty),
        defaultValue: field.defaultValue,
        dependsOn: extractFormFieldDependency(field.dependsOn),
        order: field.order,
        action: field.action,
    };
};
export const extractFormSectionConfig = (section) => {
    if (!section)
        return undefined;
    return {
        id: section.id,
        title: section.title,
        description: section.description,
        fields: section.fields?.map(extractFormFieldConfig).filter(notEmpty) ?? [],
        order: section.order,
    };
};
/**
 * Extracts ramp form configuration from API response
 */
export const extractRampFormConfiguration = (config) => {
    if (!config) {
        return undefined;
    }
    return {
        sections: config.sections
            ?.map(extractFormSectionConfig)
            .filter(notEmpty)
            ?.sort((a, b) => a.order - b.order),
        providerName: config.providerName,
        direction: config.direction,
        paymentMethod: config.paymentMethod,
        supportedCurrencies: config.supportedCurrencies?.map((currency) => Currency.toCurrency(currency)).filter(notEmpty) ?? [],
    };
};
export const extractCategorizedInstruments = (items) => {
    if (!items) {
        return [];
    }
    return items
        .map((item) => {
        const { type, instrument, hotScore, trades1h } = item;
        if (!instrument)
            return null;
        let standardized = null;
        // Handle based on the type field
        if (type === InstrumentType.FUNGIBLE_TOKEN) {
            standardized = standardizedFungibleTokenToInstrument(extractStandardizedFungibleToken(instrument));
        }
        else if (type === InstrumentType.PERPETUAL_CONTRACT) {
            standardized = standardizedPerpetualToInstrument(extractStandardizedPerpetual(instrument));
        }
        else if (type === InstrumentType.PREDICTION_MARKET) {
            standardized = standardizedPredictionEventToInstrument(extractStandardizedPredictionEvent(instrument));
        }
        // Attach the trending z-score + 1h trade count from the edge. Only
        // present on the hot-markets path; left undefined elsewhere because
        // they aren't meaningful outside that ranking. Coerce to number so
        // GraphQL scalar serialization (which can hand back a string) lands
        // on the typed contract, and drop non-numeric inputs so downstream
        // comparisons don't get NaN.
        if (standardized) {
            const score = coerceFiniteNumber(hotScore);
            if (score !== undefined)
                standardized.hotScore = score;
            const trades = coerceFiniteNumber(trades1h);
            if (trades !== undefined)
                standardized.trades1h = trades;
        }
        return standardized;
    })
        .filter((item) => item != null);
};
function coerceFiniteNumber(v) {
    if (!notEmpty(v))
        return undefined;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
}
// ============ Category Metadata Helper Functions ============
export const extractCategoryFilter = (filter) => {
    if (!filter) {
        return undefined;
    }
    return {
        id: filter.id,
        title: filter.title,
        imageUrl: filter.imageUrl,
    };
};
export const extractCategoryFilterGroup = (filterGroup) => {
    if (!filterGroup) {
        return undefined;
    }
    return {
        id: filterGroup.id,
        title: filterGroup.title,
        filters: filterGroup.filters?.map(extractCategoryFilter).filter(notEmpty) ?? [],
        layout: filterGroup.layout,
    };
};
export const extractCategorySubcategoryMetadata = (subcategory) => {
    if (!subcategory) {
        return undefined;
    }
    return {
        id: subcategory.id,
        title: subcategory.title,
        layout: subcategory.layout,
        filterGroups: subcategory.filterGroups?.map(extractCategoryFilterGroup).filter(notEmpty) ?? [],
        clientCacheKey: subcategory.clientCacheKey,
    };
};
export const extractCategoryMetadata = (category) => {
    if (!category) {
        return undefined;
    }
    return {
        id: category.id,
        title: category.title ?? "",
        subcategories: category.subcategories?.map(extractCategorySubcategoryMetadata).filter(notEmpty) ?? [],
    };
};
export const extractCategoryMetadataArray = (categories) => {
    if (!categories || !Array.isArray(categories)) {
        return [];
    }
    return categories.map(extractCategoryMetadata).filter(notEmpty);
};
const extractFilterOption = (opt) => {
    if (!opt?.id || !opt?.title)
        return undefined;
    return {
        id: opt.id,
        title: opt.title,
        timeInterval: opt.timeInterval
            ? { amount: opt.timeInterval.amount ?? 1, unit: opt.timeInterval.unit ?? "days" }
            : undefined,
        isDefault: opt.isDefault,
    };
};
const extractInstrumentSearchFilters = (filters) => {
    if (!filters)
        return { typeFilters: [], timeFilters: [], categoryFilters: [] };
    return {
        typeFilters: (filters.typeFilters ?? []).map(extractFilterOption).filter(notEmpty),
        timeFilters: (filters.timeFilters ?? []).map(extractFilterOption).filter(notEmpty),
        categoryFilters: (filters.categoryFilters ?? []).map(extractFilterOption).filter(notEmpty),
    };
};
export const extractCategoriesResponse = (data) => {
    if (!data) {
        return undefined;
    }
    const homeFilters = data.homeFilters;
    const searchFilters = data.searchFilters
        ? {
            fungibleTokens: extractInstrumentSearchFilters(data.searchFilters.fungibleTokens),
            perps: extractInstrumentSearchFilters(data.searchFilters.perps),
            predictions: extractInstrumentSearchFilters(data.searchFilters.predictions),
        }
        : undefined;
    return {
        homeFilters: {
            fungibleTokens: extractCategoryMetadataArray(homeFilters.fungibleTokens),
            perps: extractCategoryMetadataArray(homeFilters.perps),
            predictions: extractCategoryMetadataArray(homeFilters.predictions),
        },
        searchFilters,
    };
};
// ===== Order Book Extraction Functions =====
export const extractOrderBookLevel = (level) => {
    if (!level)
        return undefined;
    return {
        price: level.price,
        size: level.size,
        numOrders: level.numOrders,
    };
};
export const extractOrderBook = (orderBook) => {
    if (!orderBook)
        return undefined;
    return {
        dataTimestamp: BigInt(orderBook.dataTimestamp),
        bids: orderBook.bids?.map(extractOrderBookLevel).filter(notEmpty) ?? [],
        asks: orderBook.asks?.map(extractOrderBookLevel).filter(notEmpty) ?? [],
    };
};
export const extractOrderBookAggregation = (aggregation) => {
    if (!aggregation)
        return undefined;
    return {
        nSigFigs: aggregation.nSigFigs,
        mantissa: aggregation.mantissa,
    };
};
export const extractOrderBookTickOption = (option) => {
    if (!option)
        return undefined;
    return {
        aggregation: extractOrderBookAggregation(option.aggregation) ?? { nSigFigs: 5 },
        tickSize: option.tickSize,
        label: option.label,
    };
};
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
export const extractWalletProfile = (profile) => {
    if (!isRecord(profile))
        return undefined;
    const accountsRaw = profile.accounts;
    const accounts = Array.isArray(accountsRaw)
        ? accountsRaw.filter(isRecord).map((account) => Account.fromAccountModel(account))
        : undefined;
    const displayName = profile.displayName;
    const avatarUrl = profile.avatarUrl;
    return {
        accounts,
        displayName: typeof displayName === "string" ? displayName : undefined,
        avatarUrl: typeof avatarUrl === "string" ? avatarUrl : undefined,
    };
};
export const extractLeaderboardRow = (row) => {
    if (!isRecord(row))
        return undefined;
    const rankRaw = row.rank;
    const rank = typeof rankRaw === "number" ? rankRaw : Number(rankRaw);
    const profile = extractWalletProfile(row.profile);
    if (!profile)
        return undefined;
    return {
        rank: Number.isFinite(rank) ? rank : 0,
        profile,
        totalPnlUsd: notEmpty(row.totalPnlUsd) ? Number(row.totalPnlUsd) : undefined,
        totalTrades: notEmpty(row.totalTrades) ? Number(row.totalTrades) : undefined,
    };
};
/**
 * Compute the tick size for a price given Hyperliquid-style aggregation params.
 * tick = 10^(magnitude - nSigFigs + 1) × mantissa
 */
const calculateTickSize = (price, nSigFigs, mantissa) => {
    if (price === 0)
        return 0;
    const magnitude = Math.floor(Math.log10(Math.abs(price)));
    let tick = Math.pow(10, magnitude - nSigFigs + 1);
    if (mantissa) {
        tick *= mantissa;
    }
    return tick;
};
/**
 * Round a price down (bids) or up (asks) to the nearest tick boundary.
 */
const roundToTick = (price, tick, side) => {
    if (tick <= 0)
        return price;
    return side === "bid" ? Math.floor(price / tick) * tick : Math.ceil(price / tick) * tick;
};
/**
 * Aggregate raw orderbook levels by rounding prices to tick boundaries and
 * summing sizes. Produces the same output as Hyperliquid's server-side
 * aggregation so that live subscription data can be displayed at any tick size.
 */
export const aggregateOrderbookData = (data, aggregation) => {
    if (!data?.orderBook)
        return data;
    const midPrice = parseFloat(data.midPrice);
    const tick = calculateTickSize(midPrice || 1, aggregation.nSigFigs, aggregation.mantissa);
    if (tick <= 0)
        return data;
    const aggregateLevels = (levels, side) => {
        const buckets = new Map();
        const orderedKeys = [];
        for (const level of levels) {
            const rawPrice = parseFloat(level.price);
            const rounded = roundToTick(rawPrice, tick, side);
            const key = rounded.toFixed(10);
            const existing = buckets.get(key);
            if (existing) {
                existing.size += parseFloat(level.size);
                existing.numOrders += level.numOrders ?? 1;
            }
            else {
                buckets.set(key, { size: parseFloat(level.size), numOrders: level.numOrders ?? 1 });
                orderedKeys.push(key);
            }
        }
        return orderedKeys.map((key) => {
            const bucket = buckets.get(key);
            return {
                price: parseFloat(key).toString(),
                size: bucket.size.toString(),
                numOrders: bucket.numOrders,
            };
        });
    };
    return {
        ...data,
        orderBook: {
            ...data.orderBook,
            bids: aggregateLevels(data.orderBook.bids, "bid"),
            asks: aggregateLevels(data.orderBook.asks, "ask"),
        },
    };
};
/**
 * Merge two orderbook data sources: a server-aggregated "base" (full depth) and
 * a client-aggregated "live" overlay (near-mid, from the subscription).
 * For overlapping price levels the live data wins (it's fresher); levels only
 * present in the base are kept so the display stays full.
 */
export const mergeOrderbookData = (base, live) => {
    if (!base)
        return live;
    if (!live)
        return base;
    const mergeLevels = (baseLevels, liveLevels) => {
        // Build a map from numeric price → level using base first, then overlay live
        const map = new Map();
        const order = [];
        for (const level of baseLevels) {
            const p = parseFloat(level.price);
            map.set(p, level);
            order.push(p);
        }
        for (const level of liveLevels) {
            const p = parseFloat(level.price);
            if (!map.has(p)) {
                order.push(p);
            }
            map.set(p, level); // live overwrites base
        }
        return order.sort((a, b) => a - b).map((p) => map.get(p));
    };
    return {
        ...live, // use live metadata (spread, midPrice) — it's fresher
        tickOptions: base.tickOptions.length > 0 ? base.tickOptions : live.tickOptions,
        orderBook: {
            ...live.orderBook,
            bids: mergeLevels(base.orderBook.bids, live.orderBook.bids).reverse(),
            asks: mergeLevels(base.orderBook.asks, live.orderBook.asks),
        },
    };
};
export const extractOrderbookData = (data) => {
    if (!data)
        return undefined;
    return {
        orderBook: extractOrderBook(data.orderBook) ?? { dataTimestamp: BigInt(0), bids: [], asks: [] },
        tickOptions: data.tickOptions?.map(extractOrderBookTickOption).filter(notEmpty) ?? [],
        spread: data.spread,
        spreadPercent: data.spreadPercent,
        midPrice: data.midPrice,
    };
};
// ===== Orderbook Formatting Helper Functions =====
/**
 * Format a number as a USD currency string (e.g., "$271,218.98")
 */
const formatCurrency = (value) => {
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
/**
 * Format a number with commas for display (e.g., "1,965.21")
 * Uses up to 4 decimal places, removing trailing zeros
 */
const formatNativeAmount = (value) => {
    return value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};
/**
 * Process raw orderbook levels into intermediate format with running totals.
 * Calculates both USD values (size × price) and native token values.
 *
 * @param levels - Raw orderbook levels from the API
 * @param limit - Maximum number of levels to process
 * @returns Array of processed levels with USD and native values
 */
const processOrderBookLevels = (levels, limit) => {
    let runningTotalUsd = 0;
    let runningTotalNative = 0;
    return levels.slice(0, limit).map((level) => {
        const price = parseFloat(level.price);
        const size = parseFloat(level.size); // Native token amount
        const sizeUsd = size * price;
        runningTotalUsd += sizeUsd;
        runningTotalNative += size;
        return {
            price: level.price,
            sizeUsd,
            totalUsd: runningTotalUsd,
            sizeNative: size,
            totalNative: runningTotalNative,
        };
    });
};
/**
 * Format processed orderbook levels for UI display.
 * Converts numeric values to formatted strings for both USD and native denominations.
 *
 * @param processedLevels - Intermediate processed levels
 * @param maxTotalUsd - Maximum total USD value for depth percentage calculation
 * @returns Array of formatted orders ready for UI consumption
 */
const formatProcessedLevels = (processedLevels, maxTotalUsd) => {
    return processedLevels.map((level) => ({
        price: level.price,
        size: formatCurrency(level.sizeUsd),
        total: formatCurrency(level.totalUsd),
        sizeNative: formatNativeAmount(level.sizeNative),
        totalNative: formatNativeAmount(level.totalNative),
        depthPercent: maxTotalUsd > 0 ? level.totalUsd / maxTotalUsd : 0,
    }));
};
/**
 * Calculate the maximum total USD value across bids and asks.
 * Used for calculating depth bar percentages.
 */
const calculateMaxTotalUsd = (processedBids, processedAsks) => {
    const lastBidTotal = processedBids[processedBids.length - 1]?.totalUsd || 0;
    const lastAskTotal = processedAsks[processedAsks.length - 1]?.totalUsd || 0;
    return Math.max(lastBidTotal, lastAskTotal);
};
/**
 * Format spread values for display.
 */
const formatSpreadForDisplay = (spread, spreadPercent, midPriceNum) => {
    const spreadNum = parseFloat(spread);
    const formattedSpread = midPriceNum > 0 ? formatCurrency(spreadNum) : spread;
    const formattedSpreadPercent = `${parseFloat(spreadPercent).toFixed(3)}%`;
    return { formattedSpread, formattedSpreadPercent };
};
/**
 * Format tick options for UI display.
 */
const formatTickOptions = (tickOptions) => {
    return tickOptions.map((opt) => ({
        tickSize: opt.tickSize,
        label: opt.label,
    }));
};
/**
 * Formats raw orderbook data into a structure ready for UI display.
 * Calculates running totals, depth percentages, and formats values in both
 * USD and native token denominations for toggle support.
 *
 * @param data - Raw orderbook data from GraphQL response
 * @param limit - Maximum number of price levels to include (default: 5)
 * @returns Formatted orderbook ready for UI consumption, or undefined if no data
 */
export const formatOrderBookForDisplay = (data, limit = 5) => {
    if (!data?.orderBook) {
        return undefined;
    }
    const { orderBook, tickOptions, spread, spreadPercent, midPrice } = data;
    const midPriceNum = parseFloat(midPrice);
    // Process bids and asks with running totals
    const processedBids = processOrderBookLevels(orderBook.bids, limit);
    const processedAsks = processOrderBookLevels(orderBook.asks, limit);
    // Calculate max total for depth percentage normalization
    const maxTotalUsd = calculateMaxTotalUsd(processedBids, processedAsks);
    // Format for display
    const bids = formatProcessedLevels(processedBids, maxTotalUsd);
    const asks = formatProcessedLevels(processedAsks, maxTotalUsd);
    const { formattedSpread, formattedSpreadPercent } = formatSpreadForDisplay(spread, spreadPercent, midPriceNum);
    return {
        bids,
        asks,
        spread: formattedSpread,
        spreadPercent: formattedSpreadPercent,
        midPrice,
        tickOptions: formatTickOptions(tickOptions),
        currentTickSize: tickOptions[0]?.tickSize || "0.01",
    };
};
